import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Chip,
  Box,
  Typography
} from '@mui/material';
import { FormSection, Autocomplete } from '../../../../design-system/components/forms';
import { 
  VENDOR_CATEGORIES, 
  ALL_CATEGORIES 
} from '../../../../constants/vendorConstants';

const VendorCategoriesForm = ({ formik }) => {
  const [categoryOptions, setCategoryOptions] = useState(ALL_CATEGORIES);
  
  // Debug logging
  console.log('[VendorCategoriesForm] Initial values:', {
    categories: formik.values.categories,
    specializations: formik.values.specializations,
    type: formik.values.type
  });
  
  // Convert categories from strings to objects if needed
  const convertedCategories = React.useMemo(() => {
    if (!formik.values.categories) return [];
    
    return formik.values.categories.map(cat => {
      if (typeof cat === 'string') {
        // Find the matching option from ALL_CATEGORIES
        const option = ALL_CATEGORIES.find(opt => opt.value === cat);
        return option || { 
          value: cat.toLowerCase().replace(/ /g, '_'), // Ensure lowercase with underscores
          label: cat.replace(/_/g, ' ').split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ')
        };
      }
      return cat;
    });
  }, [formik.values.categories]);

  // Update category options based on vendor type
  useEffect(() => {
    let options = [];
    if (formik.values.type === 'influencer') {
      options = VENDOR_CATEGORIES.influencer;
    } else if (['service_provider', 'agency', 'consultant'].includes(formik.values.type)) {
      options = VENDOR_CATEGORIES.service;
    } else {
      options = ALL_CATEGORIES;
    }
    setCategoryOptions(options);
  }, [formik.values.type]);

  const handleChange = (field, value) => {
    // Handle categories value
    if (field === 'categories') {
      console.log('Categories value:', value);
      console.log('Categories type:', typeof value);
      
      // Convert categories to strings for saving (ensure lowercase)
      const categoriesAsStrings = value.map(cat => {
        const catValue = typeof cat === 'string' ? cat : cat.value;
        return catValue.toLowerCase().replace(/ /g, '_'); // Ensure lowercase with underscores
      });
      
      formik.setFieldValue(field, categoriesAsStrings);
    } else {
      formik.setFieldValue(field, value);
    }
  };

  return (
    <FormSection title="Categories & Specializations">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            label="Categories"
            value={convertedCategories}
            onChange={(value) => handleChange('categories', value)}
            options={categoryOptions}
            multiple
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option.replace(/_/g, ' ').split(' ').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ');
              }
              return option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const valueValue = typeof value === 'string' ? value : value.value;
              return optionValue === valueValue;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={typeof option === 'object' ? option.label : 
                    option.replace(/_/g, ' ').split(' ').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ')
                  }
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
            error={formik.touched.categories && Boolean(formik.errors.categories)}
            helperText={formik.touched.categories && formik.errors.categories}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Autocomplete
            label="Specializations"
            value={formik.values.specializations || []}
            onChange={(value) => handleChange('specializations', value)}
            options={[]}
            multiple
            freeSolo
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={typeof option === 'object' ? option.label : option}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
            helperText="Type and press Enter to add specializations"
            error={formik.touched.specializations && Boolean(formik.errors.specializations)}
          />
        </Grid>

        {/* Display selected categories summary */}
        {formik.values.categories && formik.values.categories.length > 0 && (
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Categories ({formik.values.categories.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formik.values.categories.map((category, index) => (
                  <Chip
                    key={index}
                    label={typeof category === 'string' ? 
                      category.replace(/_/g, ' ').split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      ).join(' ') : 
                      category.label
                    }
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </FormSection>
  );
};

VendorCategoriesForm.propTypes = {
  formik: PropTypes.object.isRequired
};

export default VendorCategoriesForm;